import { useState } from "react";

interface TableFilterControllerOptions<T> {
  filters: T[];
  identity: string;
  isMobile: boolean;
  isQuick: (filter: T) => boolean;
  toAdvanced: (filters: T[], key?: string) => T[];
  getKey: (filter: T) => string;
  getId: (filter: T) => string;
  createFilter: (key: string) => T | undefined;
  onChange: (filters: T[]) => void;
  onReset: () => void;
}

export const useTableFilterController = <T>({
  filters,
  identity,
  isMobile,
  isQuick,
  toAdvanced,
  getKey,
  getId,
  createFilter,
  onChange,
  onReset,
}: TableFilterControllerOptions<T>) => {
  const [hasOpenedPanel, setHasOpenedPanel] = useState(false);
  const [mobileMode, setMobileMode] = useState<"quick" | "custom" | null>(null);
  const [resetVersion, setResetVersion] = useState(0);
  const [focusedFilterId, setFocusedFilterId] = useState<string>();
  const [clearedIdentity, setClearedIdentity] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ source: string; filters: T[] } | null>(null);
  const isClearing = clearedIdentity === identity;
  const appliedFilters = isClearing ? [] : filters;
  const draftFilters = draft?.source === identity ? draft.filters : null;

  if (clearedIdentity !== null && clearedIdentity !== identity) {
    setClearedIdentity(null);
  }

  const openAdvanced = (key?: string, quickDraft?: T[]) => {
    const next = toAdvanced(quickDraft ?? appliedFilters, key);
    if (isMobile) {
      setDraft({ source: identity, filters: next });
    } else {
      onChange(next);
    }
    setHasOpenedPanel(true);
    setMobileMode("custom");
  };

  const openColumn = (key: string) => {
    const advanced = toAdvanced(appliedFilters);
    const existing = advanced.find((filter) => getKey(filter) === key);
    const added = existing ?? createFilter(key);
    if (added) {
      setFocusedFilterId(getId(added));
      const next = existing ? advanced : [...advanced, added];
      if (isMobile) {
        setDraft({ source: identity, filters: next });
      } else {
        onChange(next);
      }
    }
    setHasOpenedPanel(true);
    setMobileMode("custom");
  };

  const setMobileOpen = (isOpen: boolean) => {
    setMobileMode(isOpen ? "quick" : null);
    if (!isOpen) {
      setDraft(null);
      setFocusedFilterId(undefined);
    }
  };

  const reset = () => {
    setClearedIdentity(filters.length > 0 ? identity : null);
    onReset();
    setDraft(null);
    setFocusedFilterId(undefined);
    setHasOpenedPanel(false);
    setMobileMode(null);
    setResetVersion((version) => version + 1);
  };

  return {
    filters: appliedFilters,
    panelFilters: isMobile && draftFilters ? draftFilters : appliedFilters,
    isClearing,
    isAdvanced:
      hasOpenedPanel || (isMobile && mobileMode === "custom") || appliedFilters.some((filter) => !isQuick(filter)),
    mobileMode,
    resetVersion,
    focusedFilterId,
    onFilterFocused: () => setFocusedFilterId(undefined),
    setMobileOpen,
    openAdvanced,
    openColumn,
    reset,
    change: (next: T[]) => {
      setHasOpenedPanel(!isMobile || next.length > 0);
      onChange(next);
    },
    empty: () => {
      setClearedIdentity(filters.length > 0 ? identity : null);
      setHasOpenedPanel(false);
      setMobileMode(isMobile ? "quick" : null);
      setDraft(null);
      setFocusedFilterId(undefined);
    },
  };
};
